import { useEffect } from "react";

/**
 * A transient, self-dismissing informative notice (Contactos import summary
 * follow-up, 2026-09-22; restyled bottom-center with a green check per
 * REDISENO-PANEL.md §2 "Toasts", panel-redesign-v3 T1) — `role="status"`,
 * never `"alert"` (nothing here is an error the operator must act on
 * immediately). Auto-dismisses after `durationMs` (3.2s by default, per
 * §2 — ContactImportPanel's existing `durationMs={12000}` call keeps
 * overriding it), but always offers a manual close button too: a user
 * reading slower than the timer, or wanting to re-read it, must never be
 * forced to race a countdown with no way to keep it up.
 */
const DEFAULT_DURATION_MS = 3200;

export function Toast({
  message,
  onDismiss,
  durationMs = DEFAULT_DURATION_MS,
  // Codex findings review (panel-redesign-v3-04, #11): a successful create
  // whose saveAudienceFilter call then fails must not race a timer the
  // operator might not read in time — `persistent` skips the auto-dismiss
  // timer entirely, leaving only the manual close button below.
  persistent = false,
  // `variant` is purely visual (CSS hook) — `role="status"` stays the same
  // either way, since neither case is an error the operator must act on
  // immediately (that would be `role="alert"`, which this component never
  // uses).
  variant = "success",
}: {
  message: string;
  onDismiss: () => void;
  durationMs?: number;
  persistent?: boolean;
  variant?: "success" | "warning";
}) {
  useEffect(() => {
    if (persistent) return undefined;
    const timer = setTimeout(onDismiss, durationMs);
    return () => clearTimeout(timer);
  }, [onDismiss, durationMs, persistent]);

  // `message` may carry multiple `\n`-joined lines (e.g. Contactos import's
  // insertados/omitidos/rechazados breakdown) — each renders as its own
  // paragraph rather than one node with embedded line breaks, so a caller
  // can query/read a single line on its own.
  const lines = message.split("\n");

  return (
    <div className={variant === "warning" ? "v2-toast v2-toast--warning" : "v2-toast"} role="status">
      {/* Decorative — the message text already carries the meaning, same
          contract as StatusBadge/StatusPill's own dot. */}
      <svg
        className="v2-toast__check"
        aria-hidden="true"
        width="18"
        height="18"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M4 10.5l3.5 3.5L16 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <div className="v2-toast__message">
        {lines.map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>
      <button type="button" className="v2-toast__close" onClick={onDismiss} aria-label="Cerrar aviso">
        ×
      </button>
    </div>
  );
}
